import {
  Box,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ExtendedFieldError } from "product-test-types";
import React, { FC, useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";

import { ControlledTextFormField } from "../pimo-form";
import { StatusEntry } from "./status-fields";

/**
 * An entry for semaphore status with three threshold colors.
 */
export interface SemaphoreEntry {
  id?: string;
  name: string;
  label: string;
  placeholder: string;
  color?: string;
  order?: number;
  low?: { id?: string; name: string; color: string; order: number };
  medium?: { id?: string; name: string; color: string; order: number };
  high?: { id?: string; name: string; color: string; order: number };
}

export interface SemaphoreFieldsProps {
  /**
   * Initial overall reporting level.
   */
  initialReportingLevel?: "overall" | "category";
  /**
   * Initial status category entries.
   */
  initialEntries?: SemaphoreEntry[];

  initialStatusEntries?: StatusEntry[];
  /**
   * Called when entries (add/remove/update) change.
   */
  onEntriesChange?: (data: {
    entries: SemaphoreEntry[];
    level: "overall" | "category" | undefined;
  }) => void;
  onStatusEntriesChange?: (entries: StatusEntry[]) => void;
  title?: string;
  enableColorPicker?: boolean;
  validationErrors?: Record<string, ExtendedFieldError>;
  language?: string;
}

export const SemaphoreFields: FC<SemaphoreFieldsProps> = ({
  initialReportingLevel,
  initialEntries = [],
  initialStatusEntries = [],
  onEntriesChange,
  onStatusEntriesChange,
  title,
  enableColorPicker,
  validationErrors,
  language = "en",
}) => {
  const fallbackStatusEntries = useMemo<StatusEntry[]>(() => {
    if (initialStatusEntries.length > 0) {
      return initialStatusEntries;
    }

    const firstSemaphoreDefinition = initialEntries[0];
    const fallbackLevels = [
      firstSemaphoreDefinition?.low,
      firstSemaphoreDefinition?.medium,
      firstSemaphoreDefinition?.high,
    ].filter(Boolean);

    return fallbackLevels.map((level, index) => ({
      id: level?.id,
      name: level?.name ?? "",
      color: level?.color ?? "",
      label: level?.name ?? ["On Track", "At Risk", "Critical"][index],
      placeholder: level?.name ?? ["On Track", "At Risk", "Critical"][index],
      order: level?.order ?? index,
    }));
  }, [initialEntries, initialStatusEntries]);

  const fallbackCategoryEntries = useMemo<SemaphoreEntry[]>(() => {
    if (initialEntries.length > 0) {
      return initialEntries;
    }

    const fallbackLevels = fallbackStatusEntries.map((entry, index) => ({
      id: entry.id,
      name: entry.name,
      color:
        entry.color ?? ["#00C853", "#F89500", "#FF0000"][index] ?? "#00C853",
      order: entry.order ?? index,
    }));

    return [
      {
        name: getLocalizedMessage(language, "time") || "Time",
        placeholder: getLocalizedMessage(language, "time") || "Time",
        label: getLocalizedMessage(language, "time") || "Time",
        order: 0,
      },
      {
        name:
          language === "de"
            ? "Budget"
            : getLocalizedMessage(language, "approvedBudget") || "Budget",
        placeholder:
          language === "de"
            ? "Budget"
            : getLocalizedMessage(language, "approvedBudget") || "Budget",
        label:
          language === "de"
            ? "Budget"
            : getLocalizedMessage(language, "approvedBudget") || "Budget",
        order: 1,
      },
      {
        name: language === "de" ? "Umfang" : "Scope",
        placeholder: language === "de" ? "Umfang" : "Scope",
        label: language === "de" ? "Umfang" : "Scope",
        order: 2,
      },
    ].map((entry) => ({
      ...entry,
      low: fallbackLevels[0] as {
        id?: string;
        name: string;
        color: string;
        order: number;
      },
      medium: fallbackLevels[1] as {
        id?: string;
        name: string;
        color: string;
        order: number;
      },
      high: fallbackLevels[2] as {
        id?: string;
        name: string;
        color: string;
        order: number;
      },
    }));
  }, [fallbackStatusEntries, initialEntries, language]);

  const [reportingLevel, setReportingLevel] = useState<
    "overall" | "category" | undefined
  >(initialReportingLevel ?? "overall");
  const [entries, setEntries] = useState<SemaphoreEntry[] | StatusEntry[]>(
    (initialReportingLevel ?? "overall") === "category"
      ? fallbackCategoryEntries
      : fallbackStatusEntries
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Only adopt the reporting level from the parent when the prop itself
  // changes. Forcing it on every render would revert the user's selection
  // before the parent had a chance to persist it.
  const [lastReportingLevelProp, setLastReportingLevelProp] = useState(
    initialReportingLevel
  );
  if (lastReportingLevelProp !== initialReportingLevel) {
    setLastReportingLevelProp(initialReportingLevel);
    setReportingLevel(initialReportingLevel ?? "overall");
  }

  const derivedEntries =
    reportingLevel === "category"
      ? fallbackCategoryEntries
      : fallbackStatusEntries;
  if (entries !== derivedEntries) {
    setEntries(derivedEntries);
  }

  const updateEntries = (
    newEntries: SemaphoreEntry[] | StatusEntry[],
    level: "overall" | "category" | undefined
  ) => {
    setEntries(newEntries);
    setReportingLevel(level);

    if (level === "category") {
      onEntriesChange?.({ entries: newEntries as SemaphoreEntry[], level });
      return;
    }

    onStatusEntriesChange?.(newEntries as StatusEntry[]);
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLevel = e.target.value as "overall" | "category";

    if (newLevel === reportingLevel) {
      return;
    }

    setReportingLevel(newLevel);
    setEntries(
      newLevel === "category" ? fallbackCategoryEntries : fallbackStatusEntries
    );

    // The parent persists the reporting level (and derives which definition the
    // status field points at), so it has to be notified for both directions.
    onEntriesChange?.({ entries: fallbackCategoryEntries, level: newLevel });

    if (newLevel !== "category") {
      onStatusEntriesChange?.(fallbackStatusEntries);
    }
  };

  const handleNameChange = (index: number, name: string) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], name };
    updateEntries(updated, reportingLevel);
  };
  const handleColorChange = (index: number, color: string) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], color };
    updateEntries(updated, reportingLevel);
  };
  const togglePicker = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    if (!enableColorPicker) return;
    if (openIndex === index) {
      setOpenIndex(null);
      setAnchorEl(null);
    } else {
      setOpenIndex(index);
      setAnchorEl(event.currentTarget);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
        width: "100%",
        border: "1px solid #E0E0E0",
        padding: 2,
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontWeight: 400, width: "100%", fontSize: 14 }}>
            {title}
          </Typography>
          <RadioGroup row value={reportingLevel} onChange={handleLevelChange}>
            <FormControlLabel
              value="overall"
              control={<Radio />}
              labelPlacement="start"
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  Overall Status
                </Typography>
              }
            />
            <FormControlLabel
              value="category"
              labelPlacement="start"
              control={<Radio />}
              label={
                <Typography sx={{ fontSize: "14px" }}>
                  Status Categories
                </Typography>
              }
            />
          </RadioGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 14,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 500, minWidth: 280, fontSize: 14 }}>
            Status Name
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            Status Color
          </Typography>
        </Box>

        {reportingLevel === "category"
          ? entries?.map((entry, index) => (
              <Box
                key={entry.label + "semaphore"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 360 }}>
                  <ControlledTextFormField
                    value={entry.name}
                    onChange={(val) => handleNameChange(index, val)}
                    error={Boolean(
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]
                    )}
                    helperText={
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]?.message
                    }
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box
                    sx={{
                      height: 30,
                      width: 30,
                      background: "#00C853",
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      height: 30,
                      width: 30,
                      background: "#F89500",
                      borderRadius: "50%",
                    }}
                  />
                  <Box
                    sx={{
                      height: 30,
                      width: 30,

                      background: "#FF0000",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
              </Box>
            ))
          : entries?.map((entry, index) => (
              <Box
                key={entry.label + "status"}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 5,
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 360, height: "100%" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      minWidth: 280,
                      fontSize: 14,
                    }}
                  >
                    {entry.label}
                  </Typography>
                  <ControlledTextFormField
                    placeholder={entry.placeholder}
                    value={entry.name}
                    onChange={(val) => handleNameChange(index, val)}
                    error={Boolean(
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]
                    )}
                    helperText={
                      validationErrors?.[
                        `semaphoreStatusDefinition.${index}.name`
                      ]?.message
                    }
                  />
                </Box>
                <Box>
                  <Box
                    onClick={(e) => togglePicker(e, index)}
                    sx={{
                      height: 30,
                      width: 30,
                      background: entry.color,
                      "&:hover": {
                        background: `${entry.color}50`,
                      },
                      borderRadius: "50%",
                    }}
                  />
                  <Popover
                    open={openIndex === index}
                    anchorEl={anchorEl}
                    onClose={() => setOpenIndex(null)}
                    sx={{ zIndex: 2000, borderRadius: "12px" }}
                  >
                    <HexColorPicker
                      color={entry.color}
                      onChange={(color) => handleColorChange(index, color)}
                    />
                  </Popover>
                </Box>
              </Box>
            ))}
      </Box>
    </Box>
  );
};
